import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiCallWithHost } from '../../utils/apiClient'

// Async thunk to fetch progression rule templates
export const fetchProgressionRuleTemplates = createAsyncThunk(
  'progressionRuleTemplates/fetchTemplates',
  async ({ token, profileId, language = 'en' }, { rejectWithValue }) => {
    try {
      console.log(`🔍 Fetching progression rule templates in language: ${language}`)

      const response = await apiCallWithHost(
        `/v2/progression-rules/templates`,
        {
          method: 'GET',
          headers: {
            'Accept-Language': language,
          },
        },
        token,
        profileId,
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const templates = await response.json()
      console.log(
        `✅ Fetched ${templates.length} progression rule templates in language: ${language}`,
      )

      return { templates, language }
    } catch (error) {
      console.error('❌ Error fetching progression rule templates:', error)
      return rejectWithValue(error.message)
    }
  },
)

// Initial state
const initialState = {
  templates: [], // Array of ProgressionRuleTemplateDto
  templatesMap: {}, // Map by ruleType for quick lookup
  language: 'en', // Current language for the loaded templates
  loading: false,
  error: null,
}

// Slice
const progressionRuleTemplatesSlice = createSlice({
  name: 'progressionRuleTemplates',
  initialState,
  reducers: {
    clearTemplates: (state) => {
      state.templates = []
      state.templatesMap = {}
      state.error = null
    },
    loadTemplatesFromStorage: (state, action) => {
      const { templates, language } = action.payload
      state.templates = templates || []
      state.language = language || 'en'

      // Rebuild the templates map by ruleType
      state.templatesMap = {}
      templates?.forEach((template) => {
        state.templatesMap[template.ruleType] = template
      })

      state.loading = false
      state.error = null
    },
    clearTemplatesForLanguageSwitch: (state) => {
      state.templates = []
      state.templatesMap = {}
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProgressionRuleTemplates.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProgressionRuleTemplates.fulfilled, (state, action) => {
        state.loading = false
        state.templates = action.payload.templates || []
        state.language = action.payload.language || 'en'

        // Build templates map by ruleType for quick lookup
        state.templatesMap = {}
        action.payload.templates?.forEach((template) => {
          state.templatesMap[template.ruleType] = template
        })

        state.error = null
      })
      .addCase(fetchProgressionRuleTemplates.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch progression rule templates'
      })
  },
})

export const { clearTemplates, loadTemplatesFromStorage, clearTemplatesForLanguageSwitch } =
  progressionRuleTemplatesSlice.actions

// Selectors
export const selectProgressionRuleTemplates = (state) => state.progressionRuleTemplates.templates
export const selectProgressionRuleTemplatesMap = (state) =>
  state.progressionRuleTemplates.templatesMap
export const selectProgressionRuleTemplatesLoading = (state) =>
  state.progressionRuleTemplates.loading
export const selectProgressionRuleTemplatesError = (state) => state.progressionRuleTemplates.error
export const selectProgressionRuleTemplatesLanguage = (state) =>
  state.progressionRuleTemplates.language

// Selector to get template by ruleType
export const selectProgressionRuleTemplateByType = (state, ruleType) =>
  state.progressionRuleTemplates.templatesMap[ruleType]

export default progressionRuleTemplatesSlice.reducer
